import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchPath, useLocation, useNavigate } from 'react-router'

import { DirectPathBookingApplinkParams, useNavigateToDirectPathBooking } from '@lyrahealth-inc/shared-app-logic'

import { useGetIsEAPSessionCountExhausted } from './useIsEAPSessionCountExhausted'
import { getProgramCoverageBreakdown } from '../../data/customer/customerSelectors'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import {
  getIsInternationalUserWithDeclinedInternationalConsent,
  getIsUserInternational,
  getShouldUserSeeInternationalConsentForDataCollection,
} from '../../data/user/userSelectors'
import { clearDirectPathState } from '../../features/appointmentDashboard/data/appointmentDashboardActions'
import { getIsHealthPlanActive } from '../../features/healthPlan/data/healthPlanSelectors'
import {
  setDirectLinkIntent,
  setDirectLinkSource,
  setDirectLinkUtmSource,
  setIsCurrentSearchDirectPath,
} from '../../features/onboard/data/onboardDataActions'
import { getOnboardDirectLinkSource } from '../../features/onboard/data/onboardSelectors'
import {
  setSelectedTreatmentOption,
  setSelectedTreatmentOptionCTPO,
} from '../../features/onboard/treatmentOptions/data/treatmentOptionsActions'
import { TREATMENT_RECOMMENDATIONS_ROUTE } from '../constants/routingConstants'

export const useGoToDirectPathBooking = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isHealthPlanActive = useSelector(getIsHealthPlanActive)
  const getIsEAPSessionCountExhausted = useGetIsEAPSessionCountExhausted()
  const currentProgramCoverageBreakdown = useSelector(getProgramCoverageBreakdown)?.toJS()
  const currentDirectLinkSource = useSelector(getOnboardDirectLinkSource)
  const shouldSeeInternationalConsentForDataCollection = useSelector(
    getShouldUserSeeInternationalConsentForDataCollection,
  )
  const isInternationalUserWithDeclinedInternationalConsent = useSelector(
    getIsInternationalUserWithDeclinedInternationalConsent,
  )
  const isUserInternational = useSelector(getIsUserInternational)

  const navigationHandlers = useMemo(() => {
    return {
      goToLocationFinder: () => {
        // If the user navigates to a direct link search from an email they will already be on the location matcher page
        if (!matchPath(pathname, '/secure/onboard/match-location')) {
          navigate('/secure/onboard/match-location')
        }
      },
      goToOnsiteLocationFinder: () => navigate('/secure/onboard/match-location', { state: { isOnsiteSpecific: true } }),
      goToHPIPage: () => navigate('/secure/coverage-check/onboard/plan-info', { replace: true }),
      goToHPIIneligible: () => navigate('/secure/coverage-check/onboard/ineligible-direct-path', { replace: true }),
      goToInternationalConsentForDataCollection: () =>
        navigate('/secure/onboard/international-consent', {
          state: { shouldGoToDirectBookingOnConsentAcceptance: true },
        }),
      goToTreatmentRecommendations: () => navigate(TREATMENT_RECOMMENDATIONS_ROUTE),
    }
  }, [navigate, pathname])

  const navigateToDirectPath = useNavigateToDirectPathBooking({
    setSelectedTreatmentOptionCTPO: (ctpo) => dispatch(setSelectedTreatmentOptionCTPO(ctpo)),
    setSelectedTreatmentOption: (treatmentOption) => dispatch(setSelectedTreatmentOption(treatmentOption)),
    setIsCurrentSearchDirectPath: (isDirectPath) => dispatch(setIsCurrentSearchDirectPath(isDirectPath)),
    setDirectLinkIntent: (directLinkIntent) => dispatch(setDirectLinkIntent(directLinkIntent)),
    setDirectLinkSource: (directLinkSource) => dispatch(setDirectLinkSource(directLinkSource)),
    setDirectLinkUtmSource: (utm_source) => dispatch(setDirectLinkUtmSource(utm_source)),
    clearDirectPathState: () => dispatch(clearDirectPathState()),
    navigate: navigationHandlers,
  })

  return useCallback(
    ({ params }: { params: Partial<DirectPathBookingApplinkParams> }) => {
      navigateToDirectPath({
        ctpo: {
          clientele: params.clientele,
          treatment: params.treatment,
          partner: params.partner,
          offering: params.offering,
        },
        isHealthPlanActive,
        shouldSeeInternationalConsentForDataCollection,
        isInternationalUserWithDeclinedInternationalConsent,
        isUserInternational,
        treatmentOption: params.treatmentOption,
        directLinkUtmSource: params.utm_source,
        getIsEAPSessionCountExhausted,
        currentProgramCoverageBreakdown,
        directLinkSource: currentDirectLinkSource || params.directLinkSource,
        directLinkIntent: params.directLinkIntent,
      })
    },
    [
      currentDirectLinkSource,
      currentProgramCoverageBreakdown,
      getIsEAPSessionCountExhausted,
      isInternationalUserWithDeclinedInternationalConsent,
      isUserInternational,
      isHealthPlanActive,
      navigateToDirectPath,
      shouldSeeInternationalConsentForDataCollection,
    ],
  )
}
